/**
 * Global conversation-notification store (iter137).
 *
 * One singleton bound near the top of the React tree (in App.js)
 * that:
 *   1. Pulls an initial unread summary from /api/conv/unread-summary.
 *   2. Listens to the global conv WebSocket for ``message_new`` and
 *      ``message_mention`` events and bumps counters in real-time.
 *   3. Plays a sound + emits an in-tab event when a counter increments.
 *   4. Exposes ``useUnreadBadge()`` for the AccountMenu badge UI.
 *   5. ``clearAll()`` drops every counter (called when /conversations-v2
 *      mounts and we POST /mark-all-read on the server).
 *
 * Counter semantics (matching the user's pick `2c`):
 *   • ``totalUnread``    — any new message in any conv (small dim dot).
 *   • ``mentionUnread``  — @-mentions targeted at me (breathing red dot).
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { convSocket } from "@/lib/conv/socket";
import { playUnreadTick, playMentionChime } from "@/lib/conv/sounds";

class NotificationStore {
  constructor() {
    this.totalUnread = 0;
    this.mentionUnread = 0;
    this._listeners = new Set();
    this._user = null;
    this._socketOff = null;
    this._activeConvId = null; // currently focused conv (no sound for it)
    // iter140 — per-user notification preferences. Defaults match the
    // historical "everything on" behavior so a fetch failure can't
    // silently mute a user.
    this._prefs = {
      message_sounds: true,
      mention_sounds: true,
      quiet_start: "",
      quiet_end: "",
      channel: "both",
    };
  }

  // ── React subscription ──────────────────────────────────────────

  subscribe(fn) {
    this._listeners.add(fn);
    try { fn(this._snapshot()); } catch { /* noop */ }
    return () => this._listeners.delete(fn);
  }

  _snapshot() {
    return {
      totalUnread: this.totalUnread,
      mentionUnread: this.mentionUnread,
    };
  }

  _emit() {
    const snap = this._snapshot();
    this._listeners.forEach((fn) => {
      try { fn(snap); } catch { /* noop */ }
    });
  }

  // ── Lifecycle ───────────────────────────────────────────────────

  start(user) {
    // Identity changed — drop counters and refetch.
    if (user?.email !== this._user?.email) {
      this.totalUnread = 0;
      this.mentionUnread = 0;
      this._me = null;
      this._emit();
    }
    this._user = user || null;
    if (!this._user?.email) {
      this._teardownSocket();
      return;
    }
    this._fetchMe();
    this._fetchSummary();
    this._fetchPrefs();
    this._wireSocket();
  }

  stop() {
    this._user = null;
    this._me = null;
    this.totalUnread = 0;
    this.mentionUnread = 0;
    this._teardownSocket();
    this._emit();
  }

  /** The page sets this so we suppress sound + counter bumps for the
   *  conversation the user is actively reading. */
  setActiveConversation(convId) {
    this._activeConvId = convId || null;
  }

  clearAll() {
    if (this.totalUnread === 0 && this.mentionUnread === 0) return;
    this.totalUnread = 0;
    this.mentionUnread = 0;
    this._emit();
  }

  // ── Internals ───────────────────────────────────────────────────

  async _fetchSummary() {
    try {
      const { data } = await api.get("/conv/unread-summary", { silent: true });
      this.totalUnread = Math.max(0, Number(data?.total_unread) || 0);
      this.mentionUnread = Math.max(0, Number(data?.mention_unread) || 0);
      this._emit();
    } catch {
      // If we 401/403, the user just isn't a conv member — leave counters at 0.
    }
  }

  async _fetchMe() {
    if (this._me) return;
    try {
      const { data } = await api.get("/conv/me", { silent: true });
      if (data?.user_type && data?.user_id != null) {
        this._me = { user_type: data.user_type, user_id: String(data.user_id) };
      }
    } catch {
      // Non-conv users (e.g. admin without conv membership) — leave as null.
    }
  }

  async _fetchPrefs() {
    try {
      const { data } = await api.get("/conv/me/notif-prefs", { silent: true });
      if (data?.prefs) this._prefs = { ...this._prefs, ...data.prefs };
    } catch {
      // No prefs saved — keep defaults.
    }
  }

  /** True when the current local time falls inside the configured
   *  quiet-hours window (handles wrap-around midnight). */
  _isQuietNow() {
    const start = this._prefs?.quiet_start || "";
    const end = this._prefs?.quiet_end || "";
    if (!/^\d{2}:\d{2}$/.test(start) || !/^\d{2}:\d{2}$/.test(end)) return false;
    if (start === end) return false; // sentinel: empty window
    const now = new Date();
    const cur = now.getHours() * 60 + now.getMinutes();
    const [sh, sm] = start.split(":").map(Number);
    const [eh, em] = end.split(":").map(Number);
    const s = sh * 60 + sm;
    const e = eh * 60 + em;
    return s < e ? (cur >= s && cur < e) : (cur >= s || cur < e);
  }

  /** Resolve whether to play a sound + bump for a given event kind.
   *  Returns ``false`` when quiet hours, channel="none"/"email", or the
   *  per-kind toggle says don't. */
  _shouldNotify(kind /* "message" | "mention" */) {
    const ch = (this._prefs?.channel || "both").toLowerCase();
    if (ch === "none" || ch === "email") return false;
    if (this._isQuietNow()) return false;
    if (kind === "message" && !this._prefs?.message_sounds) return false;
    if (kind === "mention" && !this._prefs?.mention_sounds) return false;
    return true;
  }

  _isMyOwnSend(msg) {
    // The fan-out frame ships sender.user_type / sender.user_id (see
    // conversation_routes.py _broadcast_message_event). Compare against
    // the authoritative identity returned by /api/conv/me on boot.
    const sender = msg && msg.sender;
    if (!sender || !this._me) return false;
    return (
      sender.user_type === this._me.user_type &&
      String(sender.user_id) === String(this._me.user_id)
    );
  }

  _wireSocket() {
    if (this._socketOff) return;
    this._socketOff = convSocket.on((msg) => this._onSocketMsg(msg));
  }

  _teardownSocket() {
    if (this._socketOff) { try { this._socketOff(); } catch { /* noop */ } }
    this._socketOff = null;
  }

  _onSocketMsg(msg) {
    if (!msg || typeof msg !== "object") return;
    const type = msg.type;
    if (type === "message_new") {
      if (this._isMyOwnSend(msg)) return;
      // Suppress for the actively-open conv — the user is staring at it.
      if (msg.conv_id && msg.conv_id === this._activeConvId) return;
      this.totalUnread += 1;
      this._emit();
      // Badge always bumps so the user sees the count when they return;
      // the SOUND respects per-user prefs (quiet hours, channel, toggle).
      if (this._shouldNotify("message")) playUnreadTick();
    } else if (type === "message_mention" || type === "mention") {
      // Server may or may not emit a dedicated frame; if not, the
      // generic message_new path drives totalUnread and the bell-badge
      // refetch covers the mention count. We still react if it arrives.
      if (this._isMyOwnSend(msg)) return;
      this.mentionUnread += 1;
      this._emit();
      if (this._shouldNotify("mention")) playMentionChime();
    } else if (type === "message_recalled" || type === "messages_marked_read") {
      // Re-sync from the server — a recall or a read-receipt batch
      // may have reduced the totals in ways the client can't compute.
      this._fetchSummary();
    } else if (type === "notif_prefs_updated") {
      // Saved prefs from another tab — refetch.
      this._fetchPrefs();
    }
  }

  /** Public hook for the prefs panel to push fresh values without
   *  waiting for the next socket round-trip. */
  applyPrefsLocally(prefs) {
    if (prefs && typeof prefs === "object") {
      this._prefs = { ...this._prefs, ...prefs };
    }
  }
}

export const notificationStore = new NotificationStore();

// React hook — used by the AccountMenu badge.
export function useUnreadBadge() {
  const [snap, setSnap] = useState(() => ({
    totalUnread: notificationStore.totalUnread,
    mentionUnread: notificationStore.mentionUnread,
  }));
  useEffect(() => notificationStore.subscribe(setSnap), []);
  return snap;
}

// Dev handle.
if (typeof window !== "undefined") {
  window.__notifStore = notificationStore;  // eslint-disable-line no-underscore-dangle
}
